import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import styled from "styled-components";
import { v5 } from "@credijusto/minerals";
import { useHistory } from "react-router-dom";
import { useAuth } from "../AuthContext/AuthContext";
import AuthLayout from "../AuthLayout/AuthLayout";
import ReCAPTCHA from "react-google-recaptcha";

// Clase personalizada para errores de cancelación
class CanceledRequestError extends Error {
  constructor(message) {
    super(message);
    this.isCanceled = true;
    this.name = "CanceledRequestError";
  }
}

// Hook personalizado para verificar si el componente está montado
const useIsMounted = () => {
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  return isMounted;
};

// Estilo para el mensaje de reCAPTCHA
const RecaptchaContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin: 1rem 0;
  grid-column: 1 / -1;
`;

const RecaptchaMessage = styled.div`
  margin-top: 0.5rem;
  font-size: 0.875rem;
  font-weight: ${(props) => (props.verified ? "500" : "400")};
  color: ${(props) => props.color};
  text-align: center;
  transition: color 0.3s ease;
  width: 100%;
  max-width: 304px;
  min-height: 20px;
`;

const Card = styled.div`
  width: 100%;
  max-width: 1000px;
  box-sizing: border-box;
  z-index: 1;
  margin: 0 auto;

  @media screen and (min-width: 767px) {
    margin-left: 0.5rem;
    margin-top: 1rem;
    margin-bottom: 1rem;
    margin-right: auto;
    padding-right: 0;
    max-width: 42%;
  }

  @media screen and (min-width: 1024px) {
    margin-top: 1rem;
    margin-bottom: 1rem;
    margin-left: 1rem;
    max-width: 38%;
  }

  @media screen and (min-width: 1440px) {
    margin-top: 1rem;
    margin-bottom: 1rem;
    margin-left: 1rem;
    max-width: 42%;
  }
`;

const HeaderContainer = styled.div`
  margin-left: 0.5rem;
  width: 100%;

  @media screen and (min-width: 768px) {
    margin-left: 0.5rem;
  }

  @media screen and (min-width: 1024px) {
    margin-left: 2rem;
  }

  @media screen and (min-width: 1440px) {
    margin-left: 2.5rem;
  }
`;

const FormContainer = styled.div`
  margin-left: 0.5rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  width: 100%;

  @media screen and (min-width: 600px) {
    grid-template-columns: 1fr;
  }

  @media screen and (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-left: 0.5rem;
  }

  @media screen and (min-width: 1024px) {
    margin-left: 2rem;
    gap: 1.5rem;
  }

  @media screen and (min-width: 1440px) {
    margin-left: 2.5rem;
    gap: 1.5rem;
  }

  button[type="submit"],
  .full-width {
    grid-column: 1 / -1;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  @media screen and (max-width: 599px) {
    grid-template-columns: 1fr;
    margin-left: 0.25rem;
    gap: 1.25rem;
  }
`;

const RegisterForm = () => {
  const history = useHistory();
  const { rfc: rfcFromContext, setIdRegistro, setEmail } = useAuth();
  const rfc = rfcFromContext || localStorage.getItem("rfc");

  const isMounted = useIsMounted();
  const cancelTokenRef = useRef(axios.CancelToken.source());
  const currentPostalCodeRef = useRef("");
  const recaptchaRef = useRef();

  const [formData, setFormData] = useState({
    primerNombre: "",
    segundoNombre: "",
    apellidoPaterno: "",
    apellidoMaterno: "",
    situacionLaboral: "",
    correo: "",
    calle: "",
    numeroExterior: "",
    numeroInterior: "",
    codigoPostal: "",
    coloniaId: "",
    coloniaNombre: "",
    municipio: "",
    entidad: "",
  });

  const [colonias, setColonias] = useState([]);
  const [errorCodigoPostal, setErrorCodigoPostal] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingColonias, setIsFetchingColonias] = useState(false);
  const [errores, setErrores] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [showRfcError, setShowRfcError] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [formTriedSubmit, setFormTriedSubmit] = useState(false);

  // Estados para el reCAPTCHA (corregidos)
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [recaptchaExpired, setRecaptchaExpired] = useState(false);
  const [recaptchaStatus, setRecaptchaStatus] = useState({
    verified: false,
    message: "Marca la casilla para verificar que no eres un robot",
    color: "#6c757d",
  });

  useEffect(() => {
    return () => {
      cancelTokenRef.current.cancel("Componente desmontado");
    };
  }, []);

  useEffect(() => {
    if (!rfc) {
      history.push("/");
    }
  }, [rfc, history]);

  const validarEmail = useCallback((email) => {
    const regexEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!email) {
      return "Este campo es obligatorio";
    }

    if (!regexEmail.test(email)) {
      return "Ingresa un correo electrónico válido (ejemplo: usuario@dominio.com)";
    }

    const partesDominio = email.split("@")[1]?.split(".") || [];
    if (partesDominio.length < 2 || partesDominio.some((part) => !part)) {
      return "El dominio del correo no es válido";
    }

    return "";
  }, []);

  const areRequiredFieldsFilled = useCallback(() => {
    return (
      formData.primerNombre.trim() &&
      formData.apellidoPaterno.trim() &&
      formData.apellidoMaterno.trim() &&
      formData.situacionLaboral &&
      formData.correo &&
      !validarEmail(formData.correo) &&
      formData.calle.trim() &&
      formData.numeroExterior.trim() &&
      formData.codigoPostal.trim() &&
      /^\d{5}$/.test(formData.codigoPostal) &&
      !errorCodigoPostal &&
      formData.coloniaId &&
      formData.municipio.trim() &&
      formData.entidad.trim()
    );
  }, [formData, errorCodigoPostal, validarEmail]);

  const hasFieldErrors = useCallback(() => {
    return Object.values(errores).some((error) => error !== "");
  }, [errores]);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));

      if (name === "correo") {
        const errorEmail = validarEmail(value);
        setErrores((prev) => ({ ...prev, correo: errorEmail }));
      } else if (errores[name]) {
        setErrores((prev) => ({ ...prev, [name]: "" }));
      }

      if (name === "codigoPostal") {
        if (isMounted.current) {
          setErrorCodigoPostal("");
          setColonias([]);
          setFormData((prev) => ({
            ...prev,
            coloniaId: "",
            coloniaNombre: "",
            municipio: "",
            entidad: "",
          }));
          setErrores((prev) => ({ ...prev, coloniaId: "" }));
        }
      }
    },
    [errores, validarEmail, isMounted]
  );

  const fetchColoniasByPostalCode = useCallback(async (codigo) => {
    cancelTokenRef.current.cancel("Nueva petición iniciada");
    cancelTokenRef.current = axios.CancelToken.source();

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_VISOR_SERVER}/webapi/validar_ciec`,
        {
          accion: "codigo_postal",
          codigo_postal: codigo,
        },
        { cancelToken: cancelTokenRef.current.token }
      );
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        throw new CanceledRequestError("Request canceled");
      }
      throw error;
    }
  }, []);

  const fetchMunicipioByColonia = useCallback(
    async (codigoPostal, coloniaId) => {
      cancelTokenRef.current.cancel("Nueva petición iniciada");
      cancelTokenRef.current = axios.CancelToken.source();

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_VISOR_SERVER}/webapi/validar_ciec`,
          {
            accion: "colonia",
            codigo_postal: codigoPostal,
            colonia: coloniaId,
          },
          { cancelToken: cancelTokenRef.current.token }
        );
        return response.data;
      } catch (error) {
        if (axios.isCancel(error)) {
          throw new CanceledRequestError("Request canceled");
        }
        throw error;
      }
    },
    []
  );

  useEffect(() => {
    const fetchColonias = async () => {
      const codigo = formData.codigoPostal.trim();

      if (!/^\d{5}$/.test(codigo)) {
        if (isMounted.current) {
          setErrorCodigoPostal("El código postal debe tener 5 dígitos");
        }
        return;
      }

      currentPostalCodeRef.current = codigo;
      const currentPostalCode = codigo;

      if (isMounted.current) {
        setIsFetchingColonias(true);
        setErrorCodigoPostal("");
        setColonias([]);
        setFormData((prev) => ({
          ...prev,
          coloniaId: "",
          coloniaNombre: "",
          municipio: "",
          entidad: "",
        }));
      }

      try {
        const data = await fetchColoniasByPostalCode(codigo);

        if (
          !isMounted.current ||
          currentPostalCodeRef.current !== currentPostalCode
        ) {
          return;
        }

        if (data?.isCanceled) return;

        if (data.error) {
          if (isMounted.current) {
            setErrorCodigoPostal(data.error);
          }
          return;
        }

        if (isMounted.current) {
          if (data.colonia && typeof data.colonia === "string") {
            const coloniaUnica = {
              colonia: data.colonia,
              id: data.colonia_id || "",
            };

            setColonias([coloniaUnica]);

            setFormData((prev) => ({
              ...prev,
              coloniaId: coloniaUnica.id,
              coloniaNombre: coloniaUnica.colonia,
              municipio: data.alcaldia_municipio || "",
              entidad: data.entidad_federativa || "",
            }));
          } else if (Array.isArray(data.colonia)) {
            setColonias(data.colonia);

            if (data.colonia.length === 1) {
              const coloniaUnica = data.colonia[0];
              setFormData((prev) => ({
                ...prev,
                coloniaId: coloniaUnica.id || "",
                coloniaNombre: coloniaUnica.colonia || "",
                municipio: data.alcaldia_municipio || "",
                entidad: data.entidad_federativa || "",
              }));
            } else {
              setFormData((prev) => ({
                ...prev,
                entidad: data.entidad_federativa || "",
              }));
            }
          }
        }
      } catch (error) {
        if (
          !isMounted.current ||
          currentPostalCodeRef.current !== currentPostalCode ||
          error instanceof CanceledRequestError
        ) {
          return;
        }

        const errorMessage =
          error.response?.data?.error ||
          "Error al consultar el código postal. Intente nuevamente.";

        if (isMounted.current) {
          setErrorCodigoPostal(errorMessage);
        }
      } finally {
        if (
          isMounted.current &&
          currentPostalCodeRef.current === currentPostalCode
        ) {
          setIsFetchingColonias(false);
        }
      }
    };

    const timer = setTimeout(() => {
      if (formData.codigoPostal.trim().length === 5) {
        fetchColonias();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.codigoPostal, isMounted, fetchColoniasByPostalCode]);

  useEffect(() => {
    if (formData.coloniaId && colonias.length > 0) {
      const coloniaExists = colonias.some(
        (c) => String(c.id) === String(formData.coloniaId)
      );
      if (!coloniaExists && isMounted.current) {
        setFormData((prev) => ({
          ...prev,
          coloniaId: "",
          coloniaNombre: "",
        }));
      }
    }
  }, [colonias, formData.coloniaId, isMounted]);

  useEffect(() => {
    const fetchMunicipio = async () => {
      if (
        !formData.coloniaId ||
        !formData.codigoPostal ||
        colonias.length === 0
      ) {
        return;
      }

      try {
        if (isMounted.current) {
          setIsFetchingColonias(true);
        }

        const data = await fetchMunicipioByColonia(
          formData.codigoPostal,
          formData.coloniaId
        );

        if (!isMounted.current || data?.isCanceled) {
          return;
        }

        if (isMounted.current) {
          setFormData((prev) => ({
            ...prev,
            municipio: data.alcaldia_municipio || "",
            entidad: data.entidad_federativa || prev.entidad,
          }));
        }
      } catch (error) {
        if (!isMounted.current || error instanceof CanceledRequestError) {
          return;
        }

        if (isMounted.current) {
          setErrorCodigoPostal(
            "Error al obtener información del municipio. Intente nuevamente."
          );
        }
      } finally {
        if (isMounted.current) {
          setIsFetchingColonias(false);
        }
      }
    };

    fetchMunicipio();
  }, [
    formData.coloniaId,
    formData.codigoPostal,
    colonias,
    isMounted,
    fetchMunicipioByColonia,
  ]);

  const opcionesSituacionLaboral = [
    { label: "Empleo a tiempo completo", value: "tiempo_completo" },
    { label: "A tiempo parcial", value: "tiempo_parcial" },
    { label: "Trabajo independiente (autónomo)", value: "independiente" },
    {
      label: "Profesional con actividad económica (freelance)",
      value: "profesional_freelance",
    },
    { label: "Empresa propia", value: "empresa_propia" },
  ];

  const validarCampos = useCallback(() => {
    const nuevosErrores = {};

    if (!formData.primerNombre.trim())
      nuevosErrores.primerNombre = "Este campo es obligatorio";
    if (!formData.apellidoPaterno.trim())
      nuevosErrores.apellidoPaterno = "Este campo es obligatorio";
    if (!formData.apellidoMaterno.trim())
      nuevosErrores.apellidoMaterno = "Este campo es obligatorio";
    if (!formData.situacionLaboral)
      nuevosErrores.situacionLaboral = "Este campo es obligatorio";

    const errorEmail = validarEmail(formData.correo);
    if (errorEmail) {
      nuevosErrores.correo = errorEmail;
    }

    if (!formData.calle.trim())
      nuevosErrores.calle = "Este campo es obligatorio";
    if (!formData.numeroExterior.trim())
      nuevosErrores.numeroExterior = "Este campo es obligatorio";

    if (!formData.codigoPostal.trim()) {
      nuevosErrores.codigoPostal = "Este campo es obligatorio";
    } else if (!/^\d{5}$/.test(formData.codigoPostal)) {
      nuevosErrores.codigoPostal = "El código postal debe tener 5 dígitos";
    } else if (errorCodigoPostal) {
      nuevosErrores.codigoPostal = errorCodigoPostal;
    }

    if (!formData.coloniaId) nuevosErrores.coloniaId = "Seleccione una colonia";
    if (!formData.municipio.trim())
      nuevosErrores.municipio = "Este campo es obligatorio";
    if (!formData.entidad.trim())
      nuevosErrores.entidad = "Este campo es obligatorio";

    // Validación de reCAPTCHA - más robusta
    if (!recaptchaValue && formTriedSubmit) {
      nuevosErrores.recaptcha = "Por favor, verifica que no eres un robot";
    } else if (recaptchaExpired) {
      nuevosErrores.recaptcha =
        "La verificación ha expirado. Por favor, verifica de nuevo.";
    }

    if (isMounted.current) {
      setErrores(nuevosErrores);
    }
    return Object.keys(nuevosErrores).length === 0;
  }, [
    formData,
    errorCodigoPostal,
    recaptchaValue,
    recaptchaExpired,
    isMounted,
    validarEmail,
    formTriedSubmit,
  ]);

  useEffect(() => {
    if (formTriedSubmit) {
      validarCampos();
    }
  }, [formData, formTriedSubmit, validarCampos]);

  // Función para resetear reCAPTCHA
  const resetRecaptcha = useCallback(() => {
    setRecaptchaValue(null);
    setRecaptchaExpired(false);
    setRecaptchaStatus({
      verified: false,
      message: "Marca la casilla para verificar que no eres un robot",
      color: "#6c757d",
    });

    if (recaptchaRef.current) {
      try {
        recaptchaRef.current.reset();
      } catch (error) {
        console.warn("Error al resetear reCAPTCHA:", error);
      }
    }
  }, []);

  // Función para manejar el cambio de reCAPTCHA
  const handleRecaptchaChange = useCallback(
    (token) => {
      setRecaptchaValue(token);
      setRecaptchaExpired(false);

      // Verificar si todos los campos requeridos están completos
      const allComplete = areRequiredFieldsFilled() && !hasFieldErrors();

      setRecaptchaStatus({
        verified: true,
        message: allComplete
          ? "¡Perfecto! ¿Ya completaste todos los datos?, ahora presiona Continuar."
          : "Validación completada, pero faltan campos por completar",
        color: allComplete ? "#28a745" : "#dc3545",
      });

      // Limpiar error de reCAPTCHA si existe
      if (errores.recaptcha) {
        setErrores((prev) => ({ ...prev, recaptcha: "" }));
      }
    },
    [areRequiredFieldsFilled, hasFieldErrors, errores.recaptcha]
  );

  // Función para manejar la expiración del reCAPTCHA
  const handleRecaptchaExpired = useCallback(() => {
    // Usar setTimeout para evitar actualizaciones durante el renderizado
    setTimeout(() => {
      setRecaptchaValue(null);
      setRecaptchaExpired(true);
      setRecaptchaStatus({
        verified: false,
        message:
          "La verificación ha expirado. Por favor, marca la casilla nuevamente",
        color: "#dc3545",
      });

      // Resetear el reCAPTCHA visualmente
      if (recaptchaRef.current) {
        try {
          recaptchaRef.current.reset();
        } catch (error) {
          console.warn("Error al resetear reCAPTCHA:", error);
        }
      }
    }, 0);
  }, []);

  // Función para manejar errores del reCAPTCHA
  const handleRecaptchaError = useCallback(() => {
    setTimeout(() => {
      setRecaptchaValue(null);
      setRecaptchaStatus({
        verified: false,
        message: "Error en la verificación. Intenta nuevamente",
        color: "#dc3545",
      });
    }, 0);
  }, []);

  // Efecto para actualizar mensaje de reCAPTCHA cuando cambian los campos
  useEffect(() => {
    if (recaptchaValue) {
      const allComplete = areRequiredFieldsFilled() && !hasFieldErrors();

      setRecaptchaStatus((prev) => ({
        ...prev,
        message: allComplete
          ? "¡Perfecto! ¿Ya completaste todos los datos?, ahora presiona Continuar."
          : "Validación completada, pero faltan campos por completar",
        color: allComplete ? "#28a745" : "#dc3545",
      }));
    }
  }, [
    formData,
    errorCodigoPostal,
    errores,
    recaptchaValue,
    areRequiredFieldsFilled,
    hasFieldErrors,
  ]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setFormTriedSubmit(true);

      if (!validarCampos()) {
        // Resetear reCAPTCHA solo si hay errores relacionados
        if (recaptchaRef.current && (!recaptchaValue || recaptchaExpired)) {
          setTimeout(() => {
            resetRecaptcha();
          }, 0);
        }
        return;
      }

      if (!rfc) {
        if (isMounted.current) {
          setShowRfcError(true);
        }
        return;
      }

      if (isMounted.current) {
        setIsLoading(true);
        setApiError(null);
      }

      try {
        const payload = {
          accion: "formulario",
          rfc: rfc.trim(),
          nombre: formData.primerNombre,
          segundo_nombre: formData.segundoNombre,
          apellido_paterno: formData.apellidoPaterno,
          apellido_materno: formData.apellidoMaterno,
          situacion_laboral: formData.situacionLaboral,
          email: formData.correo,
          calle: formData.calle,
          numero_exterior: formData.numeroExterior,
          numero_interior: formData.numeroInterior || "NA",
          codigo_postal: formData.codigoPostal,
          colonia: formData.coloniaNombre,
          colonia_id: formData.coloniaId,
          municipio_o_alcaldia: formData.municipio,
          entidad_federativa: formData.entidad,
          recaptcha: recaptchaValue,
        };

        const response = await axios.post(
          `${process.env.REACT_APP_VISOR_SERVER}/webapi/validar_ciec`,
          payload,
          { cancelToken: cancelTokenRef.current.token }
        );

        if (isMounted.current) {
          if (response.data.id) {
            setIdRegistro(response.data.id);
            setEmail(formData.correo);
            localStorage.setItem("idRegistro", response.data.id);
            localStorage.setItem("userEmail", formData.correo);
            setShowSuccess(true);

            setTimeout(() => {
              if (isMounted.current) {
                history.push("/otp");
              }
            }, 3000);
          } else {
            const errorMsg =
              response.data.error ||
              "No se recibió ID de registro en la respuesta";
            setApiError(`Error en el registro: ${errorMsg}`);
          }
        }
      } catch (error) {
        if (axios.isCancel(error)) return;

        let errorMessage =
          "Ocurrió un error al enviar el formulario. Intente nuevamente.";

        if (error.response) {
          errorMessage =
            error.response.data?.error ||
            `Error del servidor: ${error.response.status} ${error.response.statusText}`;
        } else if (error.request) {
          errorMessage =
            "No se recibió respuesta del servidor. Verifica tu conexión a internet.";
        } else {
          errorMessage = `Error al configurar la solicitud: ${error.message}`;
        }

        if (isMounted.current) {
          setApiError(errorMessage);

          if (error.response?.status === 401) {
            setShowRfcError(true);
          }
        }
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    },
    [
      validarCampos,
      recaptchaValue,
      recaptchaExpired,
      rfc,
      isMounted,
      formData,
      setIdRegistro,
      setEmail,
      history,
      resetRecaptcha,
    ]
  );

  return (
    <AuthLayout>
      <v5.PopUp
        type="success"
        open={showSuccess}
        style={{
          borderRadius: "16px",
          textAlign: "center",
        }}
      >
        {
          "##### Código enviado \nEnviamos un código nuevo a tu correo electrónico. \n\nNo olvides revisar tu bandeja de correo no deseado."
        }
      </v5.PopUp>

      {apiError && (
        <v5.PopUp
          successButtonText="Entendido"
          type="error"
          onSuccess={() => setApiError(null)}
          open={!!apiError}
          style={{
            borderRadius: "16px",
            textAlign: "center",
          }}
        >
          {`##### Error \n${apiError}`}
        </v5.PopUp>
      )}

      {showRfcError && (
        <v5.PopUp
          successButtonText="Entendido"
          type="error"
          onSuccess={() => {
            setShowRfcError(false);
            history.push("/");
          }}
          open={showRfcError}
          style={{
            borderRadius: "16px",
            textAlign: "center",
          }}
        >
          {
            "##### Error \nNo se encontró el RFC. Por favor, inténtalo de nuevo."
          }
        </v5.PopUp>
      )}

      <Card>
        <HeaderContainer>
          <v5.Typography
            color="text-primary"
            font="serif"
            variant="h3"
            weight="bold"
          >
            Regístrate
          </v5.Typography>
          <v5.Typography
            color="mn-secondary"
            variant="subtitle1"
            weight="medium"
            style={{ marginBottom: "1rem" }}
          >
            Completa los siguientes datos.
          </v5.Typography>
        </HeaderContainer>

        <form onSubmit={handleSubmit}>
          <FormContainer>
            <v5.Input
              isRequired
              label="Primer nombre"
              name="primerNombre"
              placeholder="Escribe tu primer nombre"
              value={formData.primerNombre}
              onChange={handleChange}
              status={errores.primerNombre ? "error" : "default"}
              errorMessage={errores.primerNombre}
            />

            <v5.Input
              label="Segundo nombre"
              name="segundoNombre"
              placeholder="Escribe tu segundo nombre"
              value={formData.segundoNombre}
              onChange={handleChange}
            />

            <v5.Input
              isRequired
              label="Apellido paterno"
              name="apellidoPaterno"
              placeholder="Escribe tu apellido paterno"
              value={formData.apellidoPaterno}
              onChange={handleChange}
              status={errores.apellidoPaterno ? "error" : "default"}
              errorMessage={errores.apellidoPaterno}
            />

            <v5.Input
              isRequired
              label="Apellido materno"
              name="apellidoMaterno"
              placeholder="Escribe tu apellido materno"
              value={formData.apellidoMaterno}
              onChange={handleChange}
              status={errores.apellidoMaterno ? "error" : "default"}
              errorMessage={errores.apellidoMaterno}
            />

            <v5.Select
              isRequired
              label="Situación laboral"
              name="situacionLaboral"
              placeholder="Seleccione una opción"
              options={opcionesSituacionLaboral}
              value={(() => {
                const opcion = opcionesSituacionLaboral.find(
                  (opt) => opt.value === formData.situacionLaboral
                );
                return opcion ? opcion.label : formData.situacionLaboral;
              })()}
              onChangeSelect={(selected) => {
                setFormData((prev) => ({
                  ...prev,
                  situacionLaboral: selected?.value || "",
                }));
                setErrores((prev) => ({ ...prev, situacionLaboral: "" }));
              }}
              status={errores.situacionLaboral ? "error" : "default"}
              errorMessage={errores.situacionLaboral}
            />

            <v5.Input
              isRequired
              label="Correo electrónico"
              name="correo"
              type="email"
              value={formData.correo}
              onChange={handleChange}
              placeholder="Escribe tu correo electrónico (ejemplo: usuario@dominio.com)"
              status={errores.correo ? "error" : "default"}
              errorMessage={errores.correo}
            />

            <v5.Input
              isRequired
              label="Calle"
              name="calle"
              value={formData.calle}
              placeholder="Escribe tu calle"
              onChange={handleChange}
              status={errores.calle ? "error" : "default"}
              errorMessage={errores.calle}
            />

            <v5.Input
              isRequired
              label="Número exterior"
              name="numeroExterior"
              value={formData.numeroExterior}
              onChange={handleChange}
              placeholder="Escribe tu número exterior"
              status={errores.numeroExterior ? "error" : "default"}
              errorMessage={errores.numeroExterior}
            />

            <v5.Input
              label="Número interior"
              name="numeroInterior"
              value={formData.numeroInterior}
              onChange={handleChange}
              placeholder="Escribe tu número interior"
            />

            <v5.Input
              isRequired
              label="Código postal"
              name="codigoPostal"
              value={formData.codigoPostal}
              onChange={handleChange}
              placeholder="Escribe tu código postal (5 dígitos)"
              status={
                errores.codigoPostal || errorCodigoPostal
                  ? "error"
                  : isFetchingColonias
                  ? "loading"
                  : "default"
              }
              errorMessage={errorCodigoPostal || errores.codigoPostal}
              disabled={isFetchingColonias}
            />

            <v5.Select
              key={`colonia-select-${formData.codigoPostal}`}
              isRequired
              label="Colonia"
              name="coloniaId"
              placeholder={
                isFetchingColonias
                  ? "Buscando colonias..."
                  : colonias.length === 0
                  ? "Ingrese un código postal válido primero"
                  : "Seleccione una colonia"
              }
              options={colonias.map((col) => ({
                label: col.colonia || "Colonia desconocida",
                value: String(col.id || col.colonia),
              }))}
              value={
                colonias.find(
                  (c) => String(c.id) === String(formData.coloniaId)
                )?.colonia || formData.coloniaId
              }
              onChangeSelect={(selectedOption) => {
                const selectedId = selectedOption?.value?.toString() || "";
                const coloniaSeleccionada = colonias.find(
                  (c) => String(c.id) === selectedId
                );

                const isDifferentSelection =
                  String(formData.coloniaId) !== selectedId;

                setFormData((prev) => ({
                  ...prev,
                  coloniaId: selectedId,
                  coloniaNombre: coloniaSeleccionada?.colonia || "",
                  ...(colonias.length > 1 && isDifferentSelection
                    ? { municipio: "" }
                    : {}),
                }));

                setErrores((prev) => ({ ...prev, coloniaId: "" }));
              }}
              disabled={colonias.length === 0 || isFetchingColonias}
              status={errores.coloniaId ? "error" : "default"}
              errorMessage={errores.coloniaId}
            />

            <v5.Input
              isRequired
              label="Municipio/Alcaldía"
              name="municipio"
              value={formData.municipio}
              onChange={handleChange}
              placeholder="Selecciona una opción"
              disabled
              status={errores.municipio ? "error" : "default"}
              errorMessage={errores.municipio}
            />

            <v5.Input
              isRequired
              label="Entidad federativa"
              name="entidad"
              value={formData.entidad}
              onChange={handleChange}
              placeholder="Selecciona una opción"
              disabled
              status={errores.entidad ? "error" : "default"}
              errorMessage={errores.entidad}
            />

            <RecaptchaContainer>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={handleRecaptchaChange}
                onErrored={handleRecaptchaError}
                onExpired={handleRecaptchaExpired}
              />

              {/* Mensaje dinámico para reCAPTCHA */}
              <RecaptchaMessage
                verified={recaptchaStatus.verified}
                color={recaptchaStatus.color}
              >
                {recaptchaStatus.message}
              </RecaptchaMessage>

              {errores.recaptcha && (
                <v5.Typography
                  color="error"
                  variant="caption"
                  style={{
                    marginTop: "0.5rem",
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  {errores.recaptcha}
                </v5.Typography>
              )}
            </RecaptchaContainer>

            <v5.Button
              type="submit"
              disabled={
                isLoading ||
                isFetchingColonias ||
                !recaptchaValue ||
                recaptchaExpired ||
                !areRequiredFieldsFilled() ||
                hasFieldErrors()
              }
              loading={isLoading ? "true" : undefined}
            >
              {isLoading ? "Enviando..." : "Continuar"}
            </v5.Button>
          </FormContainer>
        </form>
      </Card>
    </AuthLayout>
  );
};

export default RegisterForm;
